"use client";

/**
 * One place that mounts the consent machinery: the provider, the app's trackers,
 * session replay, the banner, and the link that lets a visitor change their mind.
 *
 * Content null means the CMS could not be read or is incomplete, and no policy
 * link means the visitor cannot read what they are agreeing to. Either way there
 * is no banner AND no trackers: without a lawful way to ask, the answer is no.
 *
 * Session replay only obeys that same rule in an app whose `configureSentryClient`
 * call passes `gateReplayOnConsent`. Without it replay is already running from
 * `init` and this can stop it but not un-record what came before.
 */
import { useEffect, useMemo } from "react";

import type { ConsentContent } from "./adapter";
import { logger } from "../logger";
import { ConsentGatedReplay } from "../sentry/ConsentGatedReplay";
import { ConsentProvider } from "./ConsentProvider";
import { ConsentSettingsLink } from "./ConsentSettingsLink";
import { CookieBanner } from "./CookieBanner";

export function ConsentGateway({
  content,
  policyHref,
  trackers,
  replaySuppressed,
  floatingSettingsLink = true,
  children,
}: {
  content: ConsentContent | null;
  /** Where the policy link goes when the CMS gives no url of its own. Apps with
   * no policy page leave it out, and then the CMS url is the only source. */
  policyHref?: string;
  /** The app's own gated trackers. Rendered inside the provider, so each one
   * asks `allows()` for itself. An app with none passes nothing. */
  trackers?: React.ReactNode;
  /** True on a page this app never records, whatever the visitor answered. An
   * app with no such page leaves it out. */
  replaySuppressed?: boolean;
  /** The pill that reopens the panel. An app that draws its own every-page
   * entry (the portal's footer row) turns it off. */
  floatingSettingsLink?: boolean;
  children: React.ReactNode;
}) {
  // Memoised because the provider watches this array: a fresh array on every
  // render re-runs the effect that reads storage, which can flicker the banner.
  const offered = useMemo(
    () => content?.categories.map((c) => c.key) ?? [],
    [content],
  );

  const href = content?.policyLinkUrl ?? policyHref;

  // Complete content with nowhere to send the visitor is a content problem, not
  // an outage, so nothing upstream reports it. Silence here reads as a site
  // nobody visits rather than a consent surface switched off.
  // In an effect, not the render body: logger.error reports to Sentry, and a
  // render-phase report fires again on hydration and on every parent re-render.
  useEffect(() => {
    if (content && !href) {
      logger.error(
        "Cookie consent: the banner has no policy link, so it cannot ask",
        new Error("consent_policy_link_missing"),
      );
    }
  }, [content, href]);

  if (!content || !href) return <>{children}</>;

  return (
    <ConsentProvider offered={offered}>
      {trackers}
      {/* Gated here rather than by each app: replay reached all four web apps
          through one shared client config, so all four had the same gap.
          The suppression rides along rather than living in the app, because a
          second caller of applyReplayConsent would race this one. */}
      <ConsentGatedReplay suppressed={replaySuppressed} />
      {children}
      <CookieBanner content={content} policyHref={href} />
      {floatingSettingsLink ? (
        <ConsentSettingsLink label={content.settingsEntryLabel} />
      ) : null}
    </ConsentProvider>
  );
}
